import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  FileText, ShoppingCart, RefreshCw, AlertTriangle, CheckCircle, Users,
  TrendingUp, Clock, ArrowRightLeft, Shield, Receipt, Package, Timer,
} from 'lucide-react';
import { FilterPanel } from '@/components/FilterPanel';
import { AIPanel } from '@/components/AIPanel';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { HelpBubble } from '@/components/HelpBubble';
import { ExportDropdown } from '@/components/ExportDropdown';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useCurrency } from '@/lib/useCurrency';
import { useGlobalFilters } from '@/lib/FilterContext';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];
const AGING_COLORS = ['#22c55e', '#84cc16', '#f59e0b', '#f97316', '#ef4444'];

// fmt function moved inside component to access currency symbol

function KpiCard({ icon: Icon, label, value, sub, color = 'text-primary', testId, help }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Icon className={`w-4 h-4 ${color}`} /> {label}
          {help ? <HelpBubble text={help} /> : null}
        </div>
        <p className={`text-lg font-bold font-mono ${color}`} data-testid={testId}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function P2PAnalytics() {
  const { symbol: cs } = useCurrency();
  
  // Format function using currency symbol
  const fmt = (v) => {
    if (v == null) return cs + '0';
    if (v >= 1e6) return `${cs}${(v / 1e6).toFixed(1)}m`;
    if (v >= 1e3) return `${cs}${(v / 1e3).toFixed(0)}k`;
    return `${cs}${v?.toFixed(0) || '0'}`;
  };
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { filters, setFilters } = useGlobalFilters();

  const fetchData = async (filterObj = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filterObj).forEach(([k, v]) => { if (v) params.set(k, v); });
      const res = await api.get(`/analytics/p2p?${params}`);
      setData(res.data);
    } catch { toast.error('Failed to load P2P analytics'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(newFilters);
  };

  const kpis = data?.kpis || {};
  const invKpis = data?.invoice_kpis || {};
  const charts = data?.charts || {};

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  const poVsNonPo = [
    { name: 'PO Spend', value: kpis.po_spend || 0, fill: '#22c55e' },
    { name: 'Non-PO Spend', value: kpis.non_po_spend || 0, fill: '#ef4444' },
  ].filter(d => d.value > 0);

  const matchRateData = [
    { name: '2-Way Match', value: invKpis.two_way_match_rate || 0, fill: '#3b82f6' },
    { name: '3-Way Match', value: invKpis.three_way_match_rate || 0, fill: '#22c55e' },
    { name: 'Unmatched', value: Math.max(0, 100 - (invKpis.two_way_match_rate || 0)), fill: '#e5e7eb' },
  ];

  return (
    <div className="space-y-4" data-testid="p2p-analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight">P2P Analytics</h1>
          <p className="text-muted-foreground mt-1">Procure-to-Pay process metrics, invoice KPIs & compliance</p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="p2p" />
          <ExportDropdown data={data} pageName="P2P Analytics" />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-p2p">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageName="p2p-analytics"
        visibleFilters={['category', 'category_l2', 'category_l3', 'supplier', 'region', 'country', 'sbu', 'fiscal_year', 'cost_center', 'business_unit']}
        compact={true}
      />

      {/* Tabs */}
      <Tabs defaultValue="po-pr" className="space-y-4">
        <TabsList>
          <TabsTrigger value="po-pr" data-testid="tab-po-pr">PO/PR Overview</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="tab-invoice">Invoice KPIs</TabsTrigger>
          <TabsTrigger value="cycle" data-testid="tab-cycle">Cycle Times</TabsTrigger>
        </TabsList>

        {/* ===== TAB 1: PO/PR Overview ===== */}
        <TabsContent value="po-pr" className="space-y-4">
          {/* KPI Row 1: PO/PR */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={ShoppingCart} label="PURCHASE ORDERS" value={kpis.unique_pos?.toLocaleString() || '0'} sub={`${kpis.po_coverage_pct || 0}% of transactions`} color="text-blue-600" testId="kpi-pos" help="Number of distinct PO numbers in the current view. Coverage = share of transactions backed by a PO — the higher, the better for governance." />
            <KpiCard icon={FileText} label="PURCHASE REQS" value={kpis.unique_prs?.toLocaleString() || '0'} sub={`${kpis.pr_coverage_pct || 0}% of transactions`} color="text-indigo-600" testId="kpi-prs" help="Distinct purchase requisitions in the current view (PR number or PR creation date). Coverage = share of transactions that started with a PR — the first step of a governed procure-to-pay process." />
            <KpiCard icon={TrendingUp} label="AVG PO VALUE" value={fmt(kpis.avg_po_value)} sub={`${kpis.unique_buyers || 0} buyers`} color="text-emerald-600" testId="kpi-avg-po" help="Average value per purchase order. Very low values may signal PO splitting to bypass approval thresholds." />
            <KpiCard icon={ArrowRightLeft} label="PR-TO-PO RATE" value={`${kpis.pr_to_po_rate || 0}%`} sub="% of PRs converted to a PO" color="text-violet-600" testId="kpi-pr-po" help="Share of purchase requisitions that convert to a PO. A low rate suggests dropped or duplicated requisitions — investigate workflow bottlenecks." />
          </div>

          {/* KPI Row 2: Compliance */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={CheckCircle} label="CONTRACTED SPEND" value={`${kpis.contracted_pct || 0}%`} sub={`% of spend · ${fmt(kpis.contracted_spend ?? 0)}`} color="text-emerald-600" testId="kpi-contracted" help="Share of spend under an active framework agreement — the commercial-governance step. Target 80%+ for mature procurement." />
            <KpiCard icon={AlertTriangle} label="MAVERICK SPEND" value={`${kpis.maverick_pct || 0}%`} sub={`composite · % of transactions · ${fmt(kpis.maverick_spend ?? 0)}`} color="text-red-500" testId="kpi-maverick" help="Composite (industry-standard): NOT (contracted AND PO-backed). Off-contract = commercial leakage. Non-PO = process leakage. The breakdown strip below shows both drivers." />
            <KpiCard icon={Shield} label="2-WAY MATCH" value={`${kpis.invoice_match_rate || 0}%`} sub="PO-Invoice · % of transactions" color="text-blue-600" testId="kpi-match" help="Share of transactions whose invoice reconciles against a PO. Low rates increase overpayment and audit risk." />
            <KpiCard icon={Clock} label="PR-TO-PO CYCLE" value={kpis.avg_cycle_days != null ? `${kpis.avg_cycle_days}d` : 'N/A'} sub="avg days" color="text-amber-600" testId="kpi-cycle" help="Mean days between PR creation and PO issuance. Above 10 days usually points to approval bottlenecks or ERP data issues." />
          </div>

          {/* Maverick Breakdown Strip */}
          <Card className="bg-red-50/40 dark:bg-red-950/10 border-l-4 border-l-red-500" data-testid="maverick-breakdown-strip">
            <CardContent className="py-3">
              <div className="flex items-center gap-4 flex-wrap text-xs">
                <div className="flex items-center gap-1.5 font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  Maverick leakage breakdown:
                </div>
                <div><span className="text-muted-foreground">Composite (headline) · % of transactions:</span> <span className="font-semibold text-red-600" data-testid="maverick-strip-composite">{kpis.maverick_pct ?? 0}%</span></div>
                <div><span className="text-muted-foreground">Off-Contract (commercial) · % of spend:</span> <span className="font-medium" data-testid="maverick-strip-off-contract">{kpis.off_contract_pct ?? 0}%</span></div>
                <div><span className="text-muted-foreground">Non-PO (process) · % of transactions:</span> <span className="font-medium" data-testid="maverick-strip-non-po">{kpis.non_po_pct ?? 0}%</span></div>
                <div className="text-muted-foreground italic ml-auto">A transaction is fully governed only when both contract AND PO exist.</div>
              </div>
            </CardContent>
          </Card>

          {/* Charts Row 1: PO vs Non-PO + PO by Category */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">PO vs Non-PO Spend</CardTitle>
                <CardDescription>Spend coverage by purchase orders</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={poVsNonPo} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value"
                      label={({ x, y, percent }) => (
                        <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
                          {`${(percent * 100).toFixed(0)}%`}
                        </text>
                      )} labelLine={false}>
                      {poVsNonPo.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-4 text-xs mt-1">
                  {poVsNonPo.map((e, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: e.fill }} /> {e.name}: {fmt(e.value)}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">PO Volume by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.po_by_category || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tickFormatter={v => fmt(v)} />
                    <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Bar dataKey="spend" fill="#3b82f6" name="Spend" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2: Buyer Performance + Maverick */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">PO by Buyer</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.po_by_buyer || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="buyer" tick={{ fontSize: 10, angle: -30 }} height={60} />
                    <YAxis tickFormatter={v => fmt(v)} />
                    <Tooltip formatter={v => [fmt(v), 'Spend']} />
                    <Bar dataKey="spend" fill="#8b5cf6" name="Spend" radius={[4, 4, 0, 0]}>
                      {(charts.po_by_buyer || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" /> Maverick Spend by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.maverick_by_category || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" tickFormatter={v => fmt(v)} />
                    <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Bar dataKey="spend" fill="#ef4444" name="Maverick Spend" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== TAB 2: Invoice KPIs ===== */}
        <TabsContent value="invoice" className="space-y-4">
          {/* Invoice KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Receipt} label="UNIQUE INVOICES" value={invKpis.unique_invoices?.toLocaleString() || '0'} sub="distinct invoices" color="text-blue-600" testId="kpi-invoices" help="Distinct invoice numbers in the current view. Compare against PO count to spot uninvoiced POs or invoices without a PO." />
            <KpiCard icon={TrendingUp} label="AVG INVOICE VALUE" value={fmt(invKpis.avg_invoice_value)} sub="per invoice" color="text-emerald-600" testId="kpi-avg-inv" help="Mean value per invoice. Very low values may indicate invoice splitting to stay under approval thresholds." />
            <KpiCard icon={Shield} label="2-WAY MATCH" value={`${invKpis.two_way_match_rate || 0}%`} sub="PO + Invoice · % of transactions" color="text-blue-600" testId="kpi-2way" help="Share of transactions whose invoice is reconciled against a purchase order. Low rates increase overpayment and audit risk." />
            <KpiCard icon={CheckCircle} label="3-WAY MATCH" value={`${invKpis.three_way_match_rate || 0}%`} sub="PO + GRN + Invoice · % of transactions" color="text-emerald-600" testId="kpi-3way" help="Share of transactions whose invoice is matched against both a PO and a goods received note — the strongest payment control, confirming goods arrived before payment." />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Package} label="GRN COVERAGE" value={`${invKpis.grn_coverage_pct || 0}%`} sub={`% of transactions · ${invKpis.grn_count || 0} GRNs`} color="text-violet-600" testId="kpi-grn" help="Share of transactions with a goods received note. Low coverage weakens 3-way matching and receipt-based controls." />
            <KpiCard icon={Timer} label="AVG INVOICE AGE" value={invKpis.avg_invoice_age_days != null ? `${invKpis.avg_invoice_age_days}d` : 'N/A'} sub="days since invoiced" color="text-amber-600" testId="kpi-inv-age" help="Mean days since invoice date for invoices in view. A rising average signals a payment backlog building up." />
            <KpiCard icon={Clock} label="PO TO INVOICE" value={invKpis.avg_po_to_invoice_days != null ? `${invKpis.avg_po_to_invoice_days}d` : 'N/A'} sub="avg cycle" color="text-cyan-600" testId="kpi-po-inv" help="Mean days from PO issuance to invoice receipt. Long lags can indicate delivery delays or slow supplier invoicing." />
            <KpiCard icon={Clock} label="GRN TO INVOICE" value={invKpis.avg_grn_to_invoice_days != null ? `${invKpis.avg_grn_to_invoice_days}d` : 'N/A'} sub="avg cycle" color="text-orange-600" testId="kpi-grn-inv" help="Mean days from goods receipt to invoice. Long lags delay payment approval and can strain supplier relationships." />
          </div>

          {/* Invoice Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Invoice Aging */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Timer className="w-4 h-4 text-amber-600" /> Invoice Aging Distribution
                </CardTitle>
                <CardDescription>Days since invoice date</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.invoice_aging || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" name="Invoices" radius={[4, 4, 0, 0]}>
                      {(charts.invoice_aging || []).map((_, i) => <Cell key={i} fill={AGING_COLORS[i % AGING_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Invoice by Category */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Invoice Volume by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.invoice_by_category || []} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="category" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => [name === 'invoice_count' ? v : fmt(v), name === 'invoice_count' ? 'Invoices' : 'Spend']} />
                    <Bar dataKey="invoice_count" fill="#06b6d4" name="Invoices" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Top Suppliers by Invoice + Payment Terms */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Suppliers by Invoice Volume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(charts.invoice_by_supplier || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid="supplier-inv-row">
                      <span className="text-xs font-mono text-muted-foreground w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.supplier}</p>
                        <div className="w-full bg-muted rounded-full h-2 mt-1">
                          <div className="h-2 rounded-full" style={{
                            width: `${(s.spend / (charts.invoice_by_supplier[0]?.spend || 1)) * 100}%`,
                            backgroundColor: COLORS[i % COLORS.length]
                          }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-mono">{fmt(s.spend)}</p>
                        <p className="text-[10px] text-muted-foreground">{s.invoice_count} invoices</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Payment Terms Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.payment_terms || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="term" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(v, name) => [name === 'count' ? v : fmt(v), name === 'count' ? 'Transactions' : 'Spend']} />
                    <Bar dataKey="count" fill="#06b6d4" name="Transactions" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ===== TAB 3: Cycle Times ===== */}
        <TabsContent value="cycle" className="space-y-4">
          {/* Cycle Time KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard icon={Clock} label="PR TO PO" value={invKpis.avg_pr_to_po_days != null ? `${invKpis.avg_pr_to_po_days}d` : 'N/A'} sub="avg days" color="text-blue-600" testId="kpi-ct-pr-po" />
            <KpiCard icon={Package} label="PO TO GRN" value={invKpis.avg_po_to_grn_days != null ? `${invKpis.avg_po_to_grn_days}d` : 'N/A'} sub="avg days" color="text-emerald-600" testId="kpi-ct-po-grn" />
            <KpiCard icon={Receipt} label="GRN TO INVOICE" value={invKpis.avg_grn_to_invoice_days != null ? `${invKpis.avg_grn_to_invoice_days}d` : 'N/A'} sub="avg days" color="text-violet-600" testId="kpi-ct-grn-inv" />
            <KpiCard icon={Timer} label="PO TO INVOICE" value={invKpis.avg_po_to_invoice_days != null ? `${invKpis.avg_po_to_invoice_days}d` : 'N/A'} sub="end-to-end" color="text-amber-600" testId="kpi-ct-po-inv" />
          </div>

          {/* Visual cycle flow */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">P2P Cycle Flow</CardTitle>
              <CardDescription>Average time between each procurement stage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center justify-center gap-3 py-8">
                {[
                  { label: 'PR Created', icon: FileText, color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
                  { label: invKpis.avg_pr_to_po_days != null ? `${invKpis.avg_pr_to_po_days}d` : 'N/A', isArrow: true },
                  { label: 'PO Created', icon: ShoppingCart, color: 'bg-blue-100 text-blue-700 border-blue-300' },
                  { label: invKpis.avg_po_to_grn_days != null ? `${invKpis.avg_po_to_grn_days}d` : 'N/A', isArrow: true },
                  { label: 'GRN Received', icon: Package, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                  { label: invKpis.avg_grn_to_invoice_days != null ? `${invKpis.avg_grn_to_invoice_days}d` : 'N/A', isArrow: true },
                  { label: 'Invoice Released', icon: Receipt, color: 'bg-amber-100 text-amber-700 border-amber-300' },
                ].map((step, i) => (
                  step.isArrow ? (
                    <div key={i} className="flex flex-col items-center" data-testid="cycle-arrow">
                      <div className="text-xs font-mono font-bold text-muted-foreground">{step.label}</div>
                      <div className="text-muted-foreground text-lg">&#8594;</div>
                    </div>
                  ) : (
                    <div key={i} className={`flex flex-col items-center gap-2 px-5 py-4 rounded-xl border-2 ${step.color}`} data-testid="cycle-step">
                      <step.icon className="w-6 h-6" />
                      <span className="text-xs font-semibold">{step.label}</span>
                    </div>
                  )
                ))}
              </div>
              {(!invKpis.avg_pr_to_po_days && !invKpis.avg_po_to_grn_days && !invKpis.avg_grn_to_invoice_days) && (
                <div className="text-center text-sm text-muted-foreground bg-muted/50 rounded-lg py-3 mt-2">
                  Cycle time data requires PR Creation Date, PO Creation Date, GRN Date, and Invoice Date fields in your uploaded data.
                </div>
              )}
            </CardContent>
          </Card>

          {/* PO Suppliers + PO Trend */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top Suppliers by PO Volume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(charts.po_by_supplier || []).map((s, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid="supplier-po-row">
                      <span className="text-xs font-mono text-muted-foreground w-5">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{s.supplier}</p>
                        <div className="w-full bg-muted rounded-full h-2 mt-1">
                          <div className="h-2 rounded-full" style={{
                            width: `${(s.spend / (charts.po_by_supplier[0]?.spend || 1)) * 100}%`,
                            backgroundColor: COLORS[i % COLORS.length]
                          }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-mono">{fmt(s.spend)}</p>
                        <p className="text-[10px] text-muted-foreground">{s.po_count} POs</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">PO Trend by Period</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={charts.po_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={v => fmt(v)} />
                    <Tooltip formatter={v => [fmt(v), 'Spend']} />
                    <Bar dataKey="spend" fill="#3b82f6" name="Spend" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* AI Recommendations */}
      <AIPanel page="p2p" filters={filters} />
    </div>
  );
}
